import React, { useState, useEffect, useCallback, useRef } from 'react';
import { cemeteryAPI, demoResetAPI } from '../services/api';
import Toast from '../components/Toast';
import './ResetCemeteryPage.css';

const ACTIVE_RUN_STATUSES = ['PENDING', 'UPLOADING', 'UPLOADED', 'VALIDATING', 'PROCESSING'];

function statusClass(status) {
  if (!status) return '';
  if (status.startsWith('COMPLETED')) return 'badge-success';
  if (status === 'FAILED') return 'badge-danger';
  if (ACTIVE_RUN_STATUSES.includes(status)) return 'badge-active';
  return 'badge-muted';
}

function ResetCemeteryPage() {
  const [config, setConfig] = useState(null);
  const [cemeteries, setCemeteries] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selected, setSelected] = useState('');
  const [selectedInfo, setSelectedInfo] = useState(null);
  const [dataset, setDataset] = useState(null);
  const [runs, setRuns] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [resetting, setResetting] = useState(false);
  const [force, setForce] = useState(false);
  const [message, setMessage] = useState(null);
  const zipInputRef = useRef(null);
  const fileInputRef = useRef(null);

  // "Active" is driven by the Redis single-flight lock (authoritative, self-heals
  // via TTL). We deliberately do NOT use the import_results PROCESSING row here:
  // a task killed by the hard time limit leaves that row stuck forever and would
  // otherwise block the button permanently.
  const activeRun = Boolean(selectedInfo?.active);
  const inSchedule = config?.allowlist?.includes(selected);

  // Reset is only allowed when the feature is enabled, a usable dataset exists,
  // and no run is already in flight. Mirrors the backend run-now guards.
  const featureEnabled = Boolean(config?.enabled);
  const datasetReady = Boolean(dataset?.resettable);
  const canReset = featureEnabled && datasetReady && !activeRun && !resetting;
  let resetBlockedReason = '';
  if (!featureEnabled) resetBlockedReason = 'Reset is disabled (DEMO_RESET_ENABLED=false).';
  else if (activeRun) resetBlockedReason = 'A reset is already running for this cemetery.';
  else if (dataset && !datasetReady) resetBlockedReason = dataset.not_resettable_reason || 'No usable dataset — upload one first.';

  const loadConfig = useCallback(async () => {
    try {
      const res = await demoResetAPI.status();
      setConfig(res.data);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to load reset config' });
    }
  }, []);

  const loadCemeteries = useCallback(async () => {
    try {
      const res = await cemeteryAPI.list();
      setCemeteries(res.data.cemeteries || res.data || []);
    } catch (err) {
      // non-fatal
    }
  }, []);

  const loadSelectedInfo = useCallback(async (cem) => {
    if (!cem) { setSelectedInfo(null); return; }
    try {
      const res = await demoResetAPI.status(cem);
      setSelectedInfo(res.data.cemeteries?.[0] || null);
    } catch (err) {
      setSelectedInfo(null);
    }
  }, []);

  const loadDataset = useCallback(async (cem) => {
    if (!cem) { setDataset(null); return; }
    try {
      const res = await demoResetAPI.getDataset(cem);
      setDataset(res.data);
    } catch (err) {
      setDataset(null);
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to load dataset' });
    }
  }, []);

  const loadRuns = useCallback(async (cem) => {
    if (!cem) { setRuns([]); return; }
    try {
      const res = await demoResetAPI.listRuns({ cemetery_unique_name: cem, page_size: 20 });
      setRuns(res.data.runs || []);
    } catch (err) {
      setRuns([]);
    }
  }, []);

  useEffect(() => { loadConfig(); loadCemeteries(); }, [loadConfig, loadCemeteries]);

  // Convenience: auto-select when the env allowlist has exactly one cemetery
  useEffect(() => {
    if (!selected && config?.allowlist?.length === 1) {
      setSelected(config.allowlist[0]);
    }
  }, [config, selected]);

  useEffect(() => {
    if (selected) {
      loadSelectedInfo(selected);
      loadDataset(selected);
      loadRuns(selected);
    }
  }, [selected, loadSelectedInfo, loadDataset, loadRuns]);

  // Poll while a run is active
  useEffect(() => {
    if (!selected || !activeRun) return undefined;
    const id = setInterval(() => {
      loadSelectedInfo(selected);
      loadRuns(selected);
    }, 4000);
    return () => clearInterval(id);
  }, [selected, activeRun, loadSelectedInfo, loadRuns]);

  const filteredCemeteries = cemeteries.filter((c) =>
    c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.unique_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const selectedName = cemeteries.find((c) => c.unique_name === selected)?.name;

  const refreshAll = () => {
    loadSelectedInfo(selected);
    loadDataset(selected);
    loadRuns(selected);
  };

  const handleZipUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selected) return;
    if (!file.name.toLowerCase().endsWith('.zip')) {
      setMessage({ type: 'error', text: 'File must be a .zip' });
      return;
    }
    setUploading(true); setUploadPct(0); setMessage(null);
    try {
      const res = await demoResetAPI.uploadDataset(selected, file, setUploadPct);
      const created = res.data.created || [];
      const skipped = res.data.skipped || [];
      setMessage({
        type: 'success',
        text: `Dataset saved: ${created.length} file(s) (${created.map((c) => c.file_type).join(', ')})`
          + (skipped.length ? ` — ${skipped.length} skipped` : ''),
      });
      loadDataset(selected);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to upload ZIP' });
    } finally {
      setUploading(false);
      if (zipInputRef.current) zipInputRef.current.value = '';
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selected) return;
    setUploading(true); setUploadPct(0); setMessage(null);
    try {
      const res = await demoResetAPI.uploadDatasetFile(selected, file, setUploadPct);
      setMessage({ type: 'success', text: `Saved ${res.data.file_type}` });
      loadDataset(selected);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to upload file' });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteFile = async (f) => {
    if (!window.confirm(`Delete dataset file "${f.django_file_type}" (${f.r2_key})?`)) return;
    try {
      await demoResetAPI.deleteDatasetFile(selected, f.source_id);
      setMessage({ type: 'success', text: `Deleted ${f.django_file_type}` });
      loadDataset(selected);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to delete file' });
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm(`Delete ALL dataset files for ${selected}? This cannot be undone.`)) return;
    try {
      const res = await demoResetAPI.deleteAllDataset(selected);
      setMessage({ type: 'success', text: res.data.message || 'Dataset cleared' });
      loadDataset(selected);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to clear dataset' });
    }
  };

  const handleReset = async () => {
    if (!selected) return;
    if (!window.confirm(
      `RESET: all data for ${selected} will be DELETED and re-imported from the default dataset. ` +
      `This action cannot be undone. Continue?`
    )) return;
    setResetting(true); setMessage(null);
    try {
      const res = await demoResetAPI.runNow({ cemeteryUniqueName: selected, force });
      setMessage({ type: 'success', text: `Reset dispatched (task ${res.data.task_id}). Track it in History.` });
      setTimeout(refreshAll, 1500);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to start reset' });
    } finally {
      setResetting(false);
    }
  };

  const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

  return (
    <div className="container">
      <div className="page-header">
        <div className="page-header-text">
          <h1>Reset Cemetery</h1>
          <p>Wipe a cemetery and re-import it from its default dataset</p>
        </div>
      </div>

      <Toast message={message} onDismiss={() => setMessage(null)} />

      {/* Config / status */}
      <div className="card">
        <h2>Configuration</h2>
        {!config ? (
          <p className="text-muted">Loading…</p>
        ) : (
          <div className="rc-config">
            <span className={`badge ${config.enabled ? 'badge-success' : 'badge-muted'}`}>
              {config.enabled ? 'ENABLED' : 'DISABLED'}
            </span>
            <span className="text-muted">Env: <strong>{config.env_label}</strong></span>
            <span className="text-muted">Schedule cron: <strong>{config.cron || '(manual only)'}</strong></span>
            <span className="text-muted">Scheduled cemeteries: <strong>{config.allowlist?.length ? config.allowlist.join(', ') : '(none)'}</strong></span>
            <span className="text-muted">Plot ceiling: <strong>{config.max_existing_plots}</strong></span>
          </div>
        )}
        {config && !config.enabled && (
          <p className="text-muted rc-hint">Manual reset is blocked while DEMO_RESET_ENABLED is false. Dataset management still works.</p>
        )}
      </div>

      {/* Cemetery selection — ANY cemetery */}
      <div className="card rc-select-card">
        <h2>Select Cemetery</h2>

        {config?.allowlist?.length > 0 && (
          <div className="rc-default-block">
            <label className="text-muted">Scheduled cemeteries (from env)</label>
            <div className="rc-cemetery-list">
              {config.allowlist.map((name) => (
                <button
                  key={name}
                  className={`rc-cemetery-chip ${selected === name ? 'active' : ''}`}
                  onClick={() => { setSelected(name); setSearchQuery(''); }}
                >
                  {cemeteries.find((c) => c.unique_name === name)?.name || name}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="rc-search">
          <input
            type="text"
            className="form-control"
            placeholder={selected ? (selectedName || selected) : 'Search any cemetery…'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <div className="rc-search-results">
              {filteredCemeteries.length === 0 ? (
                <p className="text-muted" style={{ padding: '8px 12px' }}>No cemeteries found</p>
              ) : filteredCemeteries.slice(0, 30).map((c) => (
                <div
                  key={c.unique_name}
                  className="rc-search-item"
                  onClick={() => { setSelected(c.unique_name); setSearchQuery(''); }}
                >
                  <strong>{c.name}</strong>
                  <span className="text-muted">{c.unique_name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        {selected && (
          <div className="rc-selected">
            <span><strong>Selected:</strong> {selectedName || selected} <code>{selected}</code></span>
            {inSchedule && <span className="badge badge-active">in daily schedule</span>}
            <button className="btn btn-sm btn-secondary" onClick={() => setSelected('')}>Change</button>
          </div>
        )}
      </div>

      {selected && (
        <>
          {/* Dataset CRUD */}
          <div className="card">
            <div className="rc-section-head">
              <h2>Default Dataset — {selected}</h2>
              <div className="rc-actions">
                <input ref={zipInputRef} type="file" accept=".zip" onChange={handleZipUpload} disabled={uploading} style={{ display: 'none' }} id="rc-zip-input" />
                <label htmlFor="rc-zip-input" className={`btn btn-primary btn-sm ${uploading ? 'disabled' : ''}`}>
                  {uploading ? `Uploading… ${uploadPct}%` : 'Upload ZIP'}
                </label>
                <input ref={fileInputRef} type="file" accept=".csv,.geojson" onChange={handleFileUpload} disabled={uploading} style={{ display: 'none' }} id="rc-file-input" />
                <label htmlFor="rc-file-input" className={`btn btn-secondary btn-sm ${uploading ? 'disabled' : ''}`}>
                  Add / replace file
                </label>
                {dataset?.files?.length > 0 && (
                  <button className="btn btn-danger btn-sm" onClick={handleDeleteAll} disabled={uploading}>Delete all</button>
                )}
              </div>
            </div>
            <p className="text-muted">
              Upload a ZIP bundle, or add/replace one CSV/GeoJSON at a time. Supported: Plots, Persons, Interments,
              Rois, Stories, Events, Notes (CSV) and Plots/Section/Lot GEOJSON.
            </p>

            {dataset === null ? (
              <p className="text-muted">Loading dataset…</p>
            ) : dataset.files.length === 0 ? (
              <p className="text-muted">No dataset yet. Upload files to populate it.</p>
            ) : (
              <table className="rc-table">
                <thead>
                  <tr><th>Type (Django)</th><th>BIRT type</th><th>File in R2</th><th>Exists?</th><th>Used by reset?</th><th></th></tr>
                </thead>
                <tbody>
                  {dataset.files.map((f) => (
                    <tr key={f.source_id}>
                      <td>{f.django_file_type}</td>
                      <td>{f.birt_type || <span className="text-muted">—</span>}</td>
                      <td className="rc-key">{f.r2_key}</td>
                      <td>{f.exists_in_r2 ? <span className="badge badge-success">present</span> : <span className="badge badge-danger">missing</span>}</td>
                      <td>{f.supported ? <span className="badge badge-success">yes</span> : <span className="badge badge-muted">no</span>}</td>
                      <td><button className="btn btn-sm btn-danger" onClick={() => handleDeleteFile(f)}>Delete</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {dataset && (
              <p className={`text-muted rc-resettable ${dataset.resettable ? 'ok' : 'warn'}`}>
                {dataset.resettable
                  ? '✓ Dataset is ready for reset (has Plots + all files present in R2).'
                  : `⚠ Not ready for reset — ${dataset.not_resettable_reason || 'make sure a Plots file exists and all supported files are present in R2.'}`}
              </p>
            )}
          </div>

          {/* Trigger reset */}
          <div className="card rc-danger">
            <h2>Run Reset</h2>
            <p className="text-muted rc-warn-text">
              This will DELETE all data for {selected} and re-import from the default dataset. Cannot be undone.
            </p>
            {selectedInfo?.last_run && (
              <p className="text-muted">
                Last run: <span className={`badge ${statusClass(selectedInfo.last_run.status)}`}>{selectedInfo.last_run.status}</span>
                {' '}at {fmt(selectedInfo.last_run.started_at)}
              </p>
            )}
            <label className="rc-force">
              <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} />
              <span>Force (ignore "already completed today")</span>
            </label>
            {resetBlockedReason && (
              <p className="text-muted rc-warn-text">Reset unavailable: {resetBlockedReason}</p>
            )}
            <button
              className="btn btn-danger"
              onClick={handleReset}
              disabled={!canReset}
              title={resetBlockedReason || undefined}
            >
              {activeRun ? 'Reset in progress…' : resetting ? 'Starting…' : 'Reset Now'}
            </button>
          </div>

          {/* History */}
          <div className="card">
            <div className="rc-section-head">
              <h2>History</h2>
              <button className="btn btn-sm btn-secondary" onClick={() => loadRuns(selected)}>Refresh</button>
            </div>
            {runs.length === 0 ? (
              <p className="text-muted">No runs yet.</p>
            ) : (
              <table className="rc-table">
                <thead>
                  <tr><th>Status</th><th>Trigger</th><th>By</th><th>Started</th><th>Completed</th><th>Wipe</th></tr>
                </thead>
                <tbody>
                  {runs.map((r) => (
                    <tr key={r.import_result_id}>
                      <td><span className={`badge ${statusClass(r.status)}`}>{r.status}</span></td>
                      <td>{r.trigger || '—'}</td>
                      <td>{r.triggered_by || '—'}</td>
                      <td>{fmt(r.started_at)}</td>
                      <td>{fmt(r.completed_at)}</td>
                      <td className="rc-key">
                        {r.wipe_counts
                          ? Object.entries(r.wipe_counts).filter(([, v]) => v).map(([k, v]) => `${k}:${v}`).join(' ') || '0'
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default ResetCemeteryPage;
